import Typography from '@alltrails/core/components/Typography';
import classNames from 'classnames';
import Link from '@alltrails/denali/components/Link';
import { FormattedMessage } from 'react-intl';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import type Coupon from '@alltrails/types/Coupon';
import type Plans from '@alltrails/core/types/Plans';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import logUpsellProductClicked from '@alltrails/amplitude/events/logUpsellProductClicked';
import getPurchaseScreenTypePlan from '@alltrails/payment/utils/getPurchaseScreenTypePlan';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import { useExperiments } from '@alltrails/experiments';
import styles from './PlanSelection.module.scss';
import getPricingText from './getPricingText';

// Figma file: https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5541-138083&t=JJQ3VDYh6Mc0P7Ol-0

type Props = {
  className?: string;
  hideCompareLink?: boolean;
  plans: Plans;
  coupon?: Coupon;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst?: boolean;
  tierSelected: PurchaseTier;
  trigger: UpsellTrigger;
  isPromoEligible: boolean;
  subscriptionUser: SubscriptionUser | null;
};

const PlanSelection = ({
  className,
  hideCompareLink = false,
  plans,
  coupon,
  setTierSelected,
  showPlusFirst = false,
  tierSelected,
  trigger,
  isPromoEligible,
  subscriptionUser
}: Props) => {
  const currencyCode = useCurrencyCode();
  const isMobileSizedScreen = useIsMobileSizedScreen();
  const experiments = useExperiments();

  const scrollToFeatureList = () => {
    const featureListElement = document.getElementById('feature-list');
    if (featureListElement) {
      featureListElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plusPlanButton = (
    <button
      className={classNames(styles.planContainer, {
        [styles.plusSelected]: tierSelected === 'plus'
      })}
      onClick={() => {
        logUpsellProductClicked({
          product_type: 'plus',
          purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.plus, coupon),
          trigger
        });

        setTierSelected('plus');
      }}
    >
      <div className={styles.planContentContainer}>
        <PlusLogoBadge variant="full" />
        <Typography variant="text200" className={styles.description}>
          {experiments['rm-conditions-plus']?.value === 'on' ? (
            <FormattedMessage defaultMessage="Offline maps, trail conditions, and more" />
          ) : (
            <FormattedMessage defaultMessage="Offline maps, trail previews, and more" />
          )}
        </Typography>
        <Typography variant="text100">
          {getPricingText({
            plan: plans.plus,
            currencyCode,
            isPromoEligible,
            coupon: coupon?.subscriptionTier === 'plus' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
            isMobileSizedScreen,
            subscriptionUser
          })}
        </Typography>
      </div>
    </button>
  );

  return (
    <div className={classNames(styles.planSelectionWrapper, className)}>
      {!hideCompareLink && (
        <div className={styles.topRow}>
          <Typography variant="text100bold">
            <FormattedMessage defaultMessage="Choose your plan" />
          </Typography>
          <Link
            size="sm"
            variant="primary"
            onClick={() => {
              scrollToFeatureList();
            }}
          >
            <FormattedMessage defaultMessage="Compare" />
          </Link>
        </div>
      )}
      {showPlusFirst && plusPlanButton}
      <button
        className={classNames(styles.planContainer, {
          [styles.peakSelected]: tierSelected === 'peak'
        })}
        onClick={() => {
          logUpsellProductClicked({
            product_type: 'peak',
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.peak, coupon),
            trigger
          });

          setTierSelected('peak');
        }}
      >
        <div className={styles.planContentContainer}>
          <div className={styles.peakRow}>
            <PeakLogoBadge variant="full" />
            <Typography variant="text50bold" className={styles.badge}>
              <FormattedMessage defaultMessage="Includes Plus benefits" />
            </Typography>
          </div>
          <Typography variant="text200" className={styles.description}>
            {experiments['web-discover-byot-free-rm']?.value === 'on' ? (
              isMobileSizedScreen ? (
                <FormattedMessage defaultMessage="Custom routes, plant identifier, and more" />
              ) : (
                <FormattedMessage defaultMessage="Plant identifier, community heatmaps, and more" />
              )
            ) : experiments['rm-conditions-plus']?.value === 'on' ? (
              <FormattedMessage defaultMessage="Custom routes, plant identifier, and more" />
            ) : (
              <FormattedMessage defaultMessage="Trail conditions, custom routes, and more" />
            )}
          </Typography>
          <Typography variant="text100">
            {getPricingText({
              plan: plans.peak,
              currencyCode,
              isPromoEligible,
              coupon: coupon?.subscriptionTier === 'peak' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
              isMobileSizedScreen,
              subscriptionUser
            })}
          </Typography>
        </div>
      </button>
      {!showPlusFirst && plusPlanButton}
    </div>
  );
};
export default PlanSelection;
